'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Stage, Layer, Rect, Line, Group, Text, Circle, Arc } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import type Konva from 'konva';
import {
  DrawingObject, Wall, Door, Window, Tool, SnapSize,
  DEFAULT_SNAP_SIZE, DEFAULT_GRID_SIZE, Layer as LayerType,
} from '@/types';
import { getWallConstruction } from '@/lib/wall-constructions';

// ============================================
// Utils
// ============================================
const SCALE = 50;
const snap = (v: number, s: number) => Math.round(v / s) * s;
const d = (x1: number, y1: number, x2: number, y2: number) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
const id = () => Math.random().toString(36).substr(2, 9);

function snap45(sx: number, sy: number, ex: number, ey: number) {
  const dx = ex - sx, dy = ey - sy;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) return { x: ex, y: ey };
  const a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  return { x: sx + len * Math.cos(a), y: sy + len * Math.sin(a) };
}

// ============================================
// Snapping
// ============================================

function allEndpoints(walls: Wall[]) {
  const pts: { x: number; y: number; wallId: string; end: 'start' | 'end' }[] = [];
  for (const w of walls) {
    pts.push({ x: w.x1, y: w.y1, wallId: w.id, end: 'start' });
    pts.push({ x: w.x2, y: w.y2, wallId: w.id, end: 'end' });
  }
  return pts;
}

function snapEndpoint(x: number, y: number, walls: Wall[], thr = 0.15) {
  let best: { x: number; y: number } | null = null, minD = thr;
  for (const pt of allEndpoints(walls)) {
    const dd = d(x, y, pt.x, pt.y);
    if (dd < minD) { minD = dd; best = pt; }
  }
  return best;
}

function snapWallLine(x: number, y: number, walls: Wall[], ss: number, thr = 0.3) {
  let best: { x: number; y: number } | null = null, minD = thr;
  for (const w of walls) {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const ls = dx * dx + dy * dy;
    if (ls < 0.0001) continue;
    const t = Math.max(0, Math.min(1, ((x - w.x1) * dx + (y - w.y1) * dy) / ls));
    const px = w.x1 + t * dx, py = w.y1 + t * dy;
    const dd = d(x, y, px, py);
    if (dd < minD) { minD = dd; best = { x: snap(px, ss), y: snap(py, ss) }; }
  }
  return best;
}

function doSnap(x: number, y: number, walls: Wall[], ss: number) {
  const ep = snapEndpoint(x, y, walls);
  if (ep) return ep;
  const wl = snapWallLine(x, y, walls, ss);
  if (wl) return wl;
  return { x: snap(x, ss), y: snap(y, ss) };
}

// ============================================
// Wall face snapping
// ============================================

const FACE_SNAP_THRESHOLD = 3; // max distance (m) to consider the PARALLEL (room-split) snap
const FACE_SNAP_ALIGN_THRESHOLD = 0.5; // |cos(angle)| threshold for "roughly parallel"
// Only force-lock onto another wall's centerline (the PERPENDICULAR/attach
// case) within this much tighter radius. FACE_SNAP_THRESHOLD's 3m is meant
// for the "pick an offset between two side walls while drawing roughly
// parallel" feel; using the same generous radius for a hard perpendicular
// lock made the far end jump straight onto a wall's face the instant it came
// within 3m, with no way to park the point anywhere "in between".
const PERP_LOCK_THRESHOLD = 0.3;
// A wall this close to drawStart is the one the new wall is already
// attached to -- exclude it from matching, otherwise short walls snap right
// back onto the wall they're being drawn FROM.
const HOST_EXCLUDE_THRESHOLD = 0.05;

function pointOnWallLineDist(px: number, py: number, w: Wall): number {
  const wdx = w.x2 - w.x1, wdy = w.y2 - w.y1;
  const wLenSq = wdx * wdx + wdy * wdy;
  if (wLenSq < 0.0001) return Infinity;
  const t = Math.max(0, Math.min(1, ((px - w.x1) * wdx + (py - w.y1) * wdy) / wLenSq));
  return d(px, py, w.x1 + t * wdx, w.y1 + t * wdy);
}

/**
 * Find the face position of the nearest exterior wall for face-relative snapping.
 *
 * When drawing an INTERIOR wall near an exterior wall, the snap grid aligns to
 * the exterior wall's INNER face. When drawing an EXTERIOR wall, it aligns to
 * the OUTER face.
 *
 * Returns { facePos, nx, ny } where facePos is the scalar position of the wall
 * face along the normal axis (nx, ny), or null if no face snapping should occur.
 * The caller can then re-snap the perpendicular component:
 *   snapped = Math.round((cursorOnNormal - facePos) / ss) * ss + facePos
 */
function getFaceOffset(
  cursorX: number,
  cursorY: number,
  drawDx: number,
  drawDy: number,
  newWallType: string,
  walls: Wall[],
): { facePos: number; nx: number; ny: number; roomInnerWidth?: number; sideSign: number } | null {
  const extWalls = walls.filter((w) => w.construction.startsWith('ext'));
  if (extWalls.length === 0) return null;

  // Drawing direction (needed to check parallelism, and as a point known to
  // be on the "room" side of whichever wall we match, for sideSign below).
  const drawLen = Math.sqrt(drawDx * drawDx + drawDy * drawDy);
  if (drawLen < 0.001) return null;
  const drawUx = drawDx / drawLen;
  const drawUy = drawDy / drawLen;
  const refX = cursorX - drawDx, refY = cursorY - drawDy; // == drawStart

  let best: { facePos: number; nx: number; ny: number; dist: number; roomInnerWidth?: number; sideSign: number } | null = null;

  for (const w of extWalls) {
    // Skip the wall drawStart is already sitting on -- otherwise a SHORT new
    // wall snaps right back onto the very wall it's being drawn FROM instead
    // of toward wherever the cursor actually is.
    if (pointOnWallLineDist(refX, refY, w) < HOST_EXCLUDE_THRESHOLD) continue;

    const wdx = w.x2 - w.x1;
    const wdy = w.y2 - w.y1;
    const wLenSq = wdx * wdx + wdy * wdy;
    if (wLenSq < 0.0001) continue;

    // Distance from cursor to wall line
    const t = Math.max(0, Math.min(1, ((cursorX - w.x1) * wdx + (cursorY - w.y1) * wdy) / wLenSq));
    const projX = w.x1 + t * wdx;
    const projY = w.y1 + t * wdy;
    const dist = Math.sqrt((cursorX - projX) ** 2 + (cursorY - projY) ** 2);

    if (dist > FACE_SNAP_THRESHOLD) continue;

    const wallLen = Math.sqrt(wLenSq);
    const wallUx = wdx / wallLen, wallUy = wdy / wallLen;
    const cosAngle = Math.abs(drawUx * wallUx + drawUy * wallUy);
    const PARALLEL_COS = 0.7;
    const isParallel = cosAngle >= PARALLEL_COS;

    // PERPENDICULAR approach (ending flush against this wall, a normal
    // T-junction/attachment): only treat it as a genuine attach attempt
    // within the tight PERP_LOCK_THRESHOLD. FACE_SNAP_THRESHOLD's 3m is for
    // the parallel/offset feel below; using it here made the endpoint jump
    // onto the wall the instant it came within 3m, with no way to park the
    // point anywhere in between.
    if (!isParallel && dist > PERP_LOCK_THRESHOLD) continue;

    if (!best || dist < best.dist) {
      const { nx, ny } = wallNormal(w);

      // BUGFIX: target this wall's own CENTERLINE, not an inset face. The
      // previous "inner/outer face" math (a) assumed a fixed +nx/-nx side
      // that isn't reliable — two parallel exterior walls can end up with
      // the SAME wallNormal() direction if drawn the same way, so a fixed
      // sign got the room side backwards on roughly half of them — and
      // (b) didn't match how every other wall junction in this app works
      // (corners and T-junction starts both meet at a shared CENTERLINE
      // point; rendering's own half-thickness extension fills the gap to
      // the actual face). Centerline-targeting is simpler and consistent.
      const wallOnNormal = w.x1 * nx + w.y1 * ny;
      const refOnNormal = refX * nx + refY * ny;
      const sideSign = refOnNormal >= wallOnNormal ? 1 : -1;
      const facePos = wallOnNormal;

      // Only use the dynamic "split the room into two rounded sides" snap
      // when the new wall runs roughly PARALLEL to this exterior wall
      // (choosing an offset between two side walls). For the perpendicular
      // case, splitting THIS wall's own length has nothing to do with the
      // axis being snapped -- leave roomInnerWidth undefined so
      // applyFaceSnap locks straight onto the centerline instead.
      let roomInnerWidth: number | undefined;
      if (isParallel) {
        const { leftHalf: leftPerp, rightHalf: rightPerp } = perpHalvesAtEnds(w, walls);
        roomInnerWidth = Math.max(0, wallLen - leftPerp - rightPerp);
      }

      best = { facePos, nx, ny, dist, roomInnerWidth, sideSign };
    }
  }

  return best ? { facePos: best.facePos, nx: best.nx, ny: best.ny, roomInnerWidth: best.roomInnerWidth, sideSign: best.sideSign } : null;
}

/**
 * Apply face-relative snapping to a position.
 * Re-snaps the perpendicular-to-wall component so that grid lines align with
 * the wall face instead of the canvas origin.
 */
function applyFaceSnap(
  x: number,
  y: number,
  ss: number,
  faceInfo: { facePos: number; nx: number; ny: number; sideSign: number },
  newWallHalf: number = 0,
  roomInnerWidth?: number,
): { x: number; y: number } {
  const { facePos, nx, ny, sideSign } = faceInfo;
  const onNormal = x * nx + y * ny;
  let snappedOnNormal: number;

  if (roomInnerWidth === undefined) {
    // Perpendicular approach: lock exactly onto the target wall's own
    // centerline (the same "shared vertex" convention used everywhere else
    // walls meet) — nothing to round on this axis.
    snappedOnNormal = facePos;
  } else if (roomInnerWidth > 0) {
    // DYNAMIC SNAP (roughly parallel case): find position where one side is
    // grid-aligned and sum is correct. Uses the same two-candidate +
    // "widest side wins a near-tie" rule as the along-wall snap
    // (pickSplitCandidate) instead of a pure min-error pick, so this no
    // longer flips unpredictably near the midpoint.
    const adjustedFace = facePos + sideSign * newWallHalf;
    const available = roomInnerWidth - newWallHalf * 2;
    if (available > 0) {
      const distFromFace = onNormal - adjustedFace;
      const bestDist = pickSplitCandidate(distFromFace, available, ss);
      snappedOnNormal = adjustedFace + bestDist;
    } else {
      snappedOnNormal = Math.round((onNormal - adjustedFace) / ss) * ss + adjustedFace;
    }
  } else {
    const adjustedFace = facePos + sideSign * newWallHalf;
    snappedOnNormal = Math.round((onNormal - adjustedFace) / ss) * ss + adjustedFace;
  }

  const delta = snappedOnNormal - onNormal;
  return { x: Math.round((x + delta * nx) * 1e6) / 1e6, y: Math.round((y + delta * ny) * 1e6) / 1e6 };
}

// ============================================
// Dimension helpers
// ============================================

function wallThickness(wall: Wall) {
  const c = getWallConstruction(wall.construction);
  return c ? c.thickness / 1000 : 0.12;
}

/**
 * Outward-facing unit normal for a wall, accounting for a manual flip.
 * The base direction is a fixed 90° rotation of the wall's own draw
 * direction (x1,y1 -> x2,y2) — this is already self-consistent all the way
 * around a continuously-drawn perimeter (convex AND concave corners), so
 * `wall.flipped` only exists to correct the cases where that default guess
 * is wrong (disconnected chains, manual edits, mirrored copies, etc.).
 */
function wallNormal(wall: Wall): { nx: number; ny: number } {
  const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const sign = wall.flipped ? -1 : 1;
  return { nx: (-dy / len) * sign, ny: (dx / len) * sign };
}

/**
 * Half-thickness of the walls connecting at each end of `hostWall` (i.e. a
 * wall with an endpoint within `endThresh` of that end), used to go from a
 * wall's centerline length to its interior clear span. Picks the THICKEST
 * matching wall at each end (not "last one found") so this agrees with every
 * caller regardless of iteration order.
 */
function perpHalvesAtEnds(hostWall: Wall, allWalls: Wall[], endThresh = 0.15): { leftHalf: number; rightHalf: number } {
  let leftHalf = 0, rightHalf = 0;
  for (const ow of allWalls) {
    if (ow.id === hostWall.id) continue;
    const owHalf = wallThickness(ow) / 2;
    for (const pt of [{ x: ow.x1, y: ow.y1 }, { x: ow.x2, y: ow.y2 }]) {
      if (d(pt.x, pt.y, hostWall.x1, hostWall.y1) < endThresh) leftHalf = Math.max(leftHalf, owHalf);
      if (d(pt.x, pt.y, hostWall.x2, hostWall.y2) < endThresh) rightHalf = Math.max(rightHalf, owHalf);
    }
  }
  return { leftHalf, rightHalf };
}

/**
 * Split a span of length `available` into [left, available - left] at the
 * raw (unsnapped) left-distance `rawLeft`, snapping to grid `ss`.
 * Two candidates are considered — round the LEFT side (right = remainder),
 * or round the RIGHT side (left = remainder) — and whichever is numerically
 * closer to the raw cursor position wins, so the snap pulls in from either
 * direction depending on where the cursor actually is. On a near-tie (cursor
 * close to the midpoint), the side that is currently WIDER gets to be the
 * round one — an even split usually matters most for the bigger room.
 */
function pickSplitCandidate(rawLeft: number, available: number, ss: number, tieEps = 0.02): number {
  if (available <= 0) return Math.round(rawLeft / ss) * ss;
  const rawRight = available - rawLeft;
  // NOTE: deliberately NOT clamped to [0, available] here. The face-snap
  // caller can legitimately pass a rawLeft far outside that range (the
  // matched exterior wall's "available" span isn't always the axis the
  // cursor is actually moving along), and clamping the candidates to the
  // span's edges created a hard discontinuity — the snap would suddenly
  // jump to the opposite boundary instead of continuing to track the
  // cursor. Unclamped, both candidates stay close to rawLeft and the pick
  // stays continuous; callers that need a bounded result (e.g.
  // snapAlongHostWall) clamp rawLeft themselves before calling.
  const leftIfA = Math.round(rawLeft / ss) * ss;
  const rightIfB = Math.round(rawRight / ss) * ss;
  const leftIfB = available - rightIfB;
  const errA = Math.abs(leftIfA - rawLeft);
  const errB = Math.abs(leftIfB - rawLeft);
  if (Math.abs(errA - errB) <= tieEps) {
    return rawLeft >= rawRight ? leftIfA : leftIfB;
  }
  return errA <= errB ? leftIfA : leftIfB;
}

/**
 * When the cursor rests on the MIDDLE of an existing wall (about to start a
 * new wall there, i.e. a T-junction), snap that position relative to the
 * host wall's own interior corners instead of the absolute canvas grid —
 * otherwise splitting a wall's clear span gives arbitrary decimals (e.g.
 * 5.213 / 3.821) instead of round numbers, since the interior corner itself
 * sits half a wall-thickness off the absolute grid. Only meaningful for
 * placing the START of a new wall (see call sites).
 */
function snapAlongHostWall(
  cursorX: number, cursorY: number, walls: Wall[], ss: number, newWallThickness: number, thr = 0.3,
): { x: number; y: number } | null {
  const newWallHalf = newWallThickness / 2;
  let best: { x: number; y: number; dist: number } | null = null;

  for (const w of walls) {
    const wdx = w.x2 - w.x1, wdy = w.y2 - w.y1;
    const wLenSq = wdx * wdx + wdy * wdy;
    if (wLenSq < 0.0001) continue;
    const wLen = Math.sqrt(wLenSq);
    const t = ((cursorX - w.x1) * wdx + (cursorY - w.y1) * wdy) / wLenSq;
    if (t < 0.02 || t > 0.98) continue; // leave endpoints to the existing endpoint-snap

    const projX = w.x1 + t * wdx, projY = w.y1 + t * wdy;
    const dist = d(cursorX, cursorY, projX, projY);
    if (dist > thr) continue;
    if (best && dist >= best.dist) continue;

    const { leftHalf, rightHalf } = perpHalvesAtEnds(w, walls);
    const innerSpan = Math.max(0, wLen - leftHalf - rightHalf); // this wall's "I" dimension
    const available = innerSpan - newWallThickness;             // minus the new wall's own thickness
    if (available <= 0) continue;

    const rawCenterFromLeftFace = Math.max(0, Math.min(innerSpan, t * wLen - leftHalf));
    const rawLeftRoom = Math.max(0, Math.min(available, rawCenterFromLeftFace - newWallHalf));
    const snappedLeftRoom = pickSplitCandidate(rawLeftRoom, available, ss);
    const snappedCenterFromLeftFace = snappedLeftRoom + newWallHalf;
    const snapT = (leftHalf + snappedCenterFromLeftFace) / wLen;

    // Reverted: an earlier version offset this point off the host wall's
    // centerline (toward whichever side the raw cursor happened to be on)
    // to match the far end's face-inset convention. That pre-commits the new
    // wall to ONE side at the moment of the first click and visibly sits off
    // the wall — not what's wanted. Snap to the host wall's own centerline
    // instead, same as an ordinary corner: this keeps the T-junction
    // perfectly centered on the host wall and free to be dragged either
    // direction (e.g. up or down off a horizontal wall) afterwards.
    best = { x: w.x1 + snapT * wdx, y: w.y1 + snapT * wdy, dist };
  }

  return best ? { x: Math.round(best.x * 1e6) / 1e6, y: Math.round(best.y * 1e6) / 1e6 } : null;
}

/**
 * Returns this wall's own HALF-thickness if a genuine CORNER exists at
 * (px, py) — i.e. another EXTERIOR wall ends there AND turns (isn't just a
 * collinear continuation of the same straight run) — or 0 if nothing is
 * there, or what's there is only an INTERIOR wall butting in (T-junction),
 * or the "other" exterior wall is actually just the other half of THIS
 * same wall after a T-junction physically split it into two Wall records.
 * A T-junction/continuation needs NO length adjustment: this wall's own
 * face runs straight through that point untouched. Only a real turn
 * (where an exterior wall corner is formed) needs the half-thickness
 * extension (outer) / inset (inner) that fills that corner in visually.
 */
function cornerAdjustment(wall: Wall, px: number, py: number, allWalls: DrawingObject[]): number {
  const thick = wallThickness(wall);
  const wdx = wall.x2 - wall.x1, wdy = wall.y2 - wall.y1;
  const wLen = Math.hypot(wdx, wdy);
  if (wLen < 0.0001) return 0;
  for (const obj of allWalls) {
    if (obj.type !== 'wall' || obj.id === wall.id) continue;
    const ow = obj as Wall;
    if (!ow.construction.startsWith('ext')) continue; // only ext walls form a true corner
    const dd = Math.min(
      Math.sqrt((ow.x1 - px) ** 2 + (ow.y1 - py) ** 2),
      Math.sqrt((ow.x2 - px) ** 2 + (ow.y2 - py) ** 2),
    );
    if (dd > 0.15) continue;
    const odx = ow.x2 - ow.x1, ody = ow.y2 - ow.y1;
    const oLen = Math.hypot(odx, ody);
    if (oLen < 0.0001) continue;
    // Roughly collinear with THIS wall => it's the other half of the same
    // straight run (split at a T-junction), not a turning corner. Skip it.
    const cosAngle = Math.abs((wdx * odx + wdy * ody) / (wLen * oLen));
    if (cosAngle > 0.7) continue;
    return thick / 2;
  }
  return 0;
}

/**
 * Finds the wall (of any construction type) that connects at (px, py),
 * ignoring a collinear continuation of this same wall (same logic as
 * cornerAdjustment's corner check, just returning the wall itself instead
 * of a precomputed amount — used where the caller needs that other wall's
 * own thickness, not just "is there a corner here").
 *
 * BUGFIX: checks BOTH (a) another wall's ENDPOINT near (px,py) — a corner,
 * two walls terminating at the same point — AND (b) (px,py) lying on the
 * MIDDLE of another wall's own line span — a T-junction into a wall that
 * was never physically split (exterior walls no longer get split at all;
 * see trySplitWallAt). Checking endpoints only (the old behaviour) missed
 * every T-junction into a whole exterior wall entirely, silently falling
 * back to the "free end" case and producing a centerline-based number
 * instead of the correct face-based one.
 */
function findConnectingWall(wall: Wall, px: number, py: number, allWalls: DrawingObject[]): Wall | null {
  return findConnectingWallAt(px, py, wall.x2 - wall.x1, wall.y2 - wall.y1, wall.id, allWalls);
}

/**
 * Core of findConnectingWall, usable BEFORE a Wall record exists yet — the
 * live drag preview/snap needs this exact same "what's really at this
 * point" check while the user is still dragging a brand-new wall, using
 * its current draw direction (dirX,dirY) instead of a finished wall's own
 * x1/x2. Keeping ONE implementation means the live preview and the final
 * render can never again drift apart (that mismatch was the whole "4000
 * while dragging becomes something else once placed" class of bug).
 */
function findConnectingWallAt(
  px: number, py: number, dirX: number, dirY: number, excludeId: string | null, allWalls: DrawingObject[],
): Wall | null {
  const dLen = Math.hypot(dirX, dirY);
  for (const obj of allWalls) {
    if (obj.type !== 'wall' || (excludeId && obj.id === excludeId)) continue;
    const ow = obj as Wall;
    const nearEndpoint = Math.min(
      Math.sqrt((ow.x1 - px) ** 2 + (ow.y1 - py) ** 2),
      Math.sqrt((ow.x2 - px) ** 2 + (ow.y2 - py) ** 2),
    ) <= 0.15;
    let nearMidline = false;
    if (!nearEndpoint) {
      const odx = ow.x2 - ow.x1, ody = ow.y2 - ow.y1;
      const oLenSq = odx * odx + ody * ody;
      if (oLenSq > 0.0001) {
        const t = ((px - ow.x1) * odx + (py - ow.y1) * ody) / oLenSq;
        if (t > 0.02 && t < 0.98) {
          const projX = ow.x1 + t * odx, projY = ow.y1 + t * ody;
          nearMidline = Math.sqrt((px - projX) ** 2 + (py - projY) ** 2) <= 0.15;
        }
      }
    }
    if (!nearEndpoint && !nearMidline) continue;
    if (dLen > 0.0001) {
      const odx = ow.x2 - ow.x1, ody = ow.y2 - ow.y1;
      const oLen = Math.hypot(odx, ody);
      if (oLen > 0.0001) {
        const cosAngle = Math.abs((dirX * odx + dirY * ody) / (dLen * oLen));
        if (cosAngle > 0.7) continue; // collinear continuation, not a real junction
      }
    }
    return ow;
  }
  return null;
}

/**
 * Which side of an interior wall faces the "main" bulk of the building.
 * An interior wall's own draw direction is arbitrary, so wallNormal()'s
 * sign has no inherent relationship to "which side is the bigger room" —
 * unlike exterior walls, where the direction is self-consistent all the
 * way around a continuously-drawn perimeter. Approximates "where most of
 * the building is" as the centroid of every wall's midpoint, and returns
 * +1/-1 to flip nx/ny so I (clear/inner) consistently faces that side.
 */
function roomSideSign(wall: Wall, allWalls: DrawingObject[]): number {
  const { nx, ny } = wallNormal(wall);
  const wallOnNormal = wall.x1 * nx + wall.y1 * ny;
  let cx = 0, cy = 0, n = 0;
  for (const obj of allWalls) {
    if (obj.type !== 'wall') continue;
    const w = obj as Wall;
    cx += (w.x1 + w.x2) / 2;
    cy += (w.y1 + w.y2) / 2;
    n++;
  }
  if (n === 0) return 1;
  cx /= n; cy /= n;
  const centroidOnNormal = cx * nx + cy * ny;
  return centroidOnNormal >= wallOnNormal ? 1 : -1;
}

// Check if a point is near a wall line (for splitting)
function splitWallAt(wall: Wall, px: number, py: number, thr = 0.15): number | null {
  const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
  const ls = dx * dx + dy * dy;
  if (ls < 0.0001) return null;
  const t = ((px - wall.x1) * dx + (py - wall.y1) * dy) / ls;
  if (t < 0.05 || t > 0.95) return null; // too close to ends
  const projX = wall.x1 + t * dx, projY = wall.y1 + t * dy;
  if (d(px, py, projX, projY) > 0.15) return null;
  return t;
}

// Split-at-start was previously skipped — only the END point of a newly drawn
// wall could split an existing wall. T-junctions started from the MIDDLE of an
// existing wall (drawStart on a wall) never got a connecting endpoint there,
// which broke endpoint-based thickness/room-width lookups used by face snapping
// and the live dimension indicator. Apply this to BOTH the start and end point.
function trySplitWallAt(
  wallsArr: Wall[],
  nonWallsArr: DrawingObject[],
  px: number,
  py: number,
): { walls: Wall[]; nonWalls: DrawingObject[] } {
  for (const w of wallsArr) {
    // BUGFIX: don't fragment exterior walls into separate Wall records just
    // because an interior wall T-junctions into them. A 10×10m exterior
    // wall should still show its own overall "Y 10.000 / I 9.176" as ONE
    // wall no matter how many interior walls get added later — splitting it
    // into pieces lost that overall number entirely (only per-piece
    // sub-measurements remained). The dimension-rendering code already
    // detects interior walls touching an exterior wall's middle and draws
    // the segment breakdown dynamically, without needing the exterior
    // wall's own data actually cut apart. Interior walls can still be
    // split by each other as before — this only protects ext walls.
    if (w.construction.startsWith('ext')) continue;
    const t = splitWallAt(w, px, py);
    if (t === null) continue;
    const midX = w.x1 + (w.x2 - w.x1) * t;
    const midY = w.y1 + (w.y2 - w.y1) * t;
    const w1: Wall = { ...w, id: id(), x2: midX, y2: midY };
    const w2: Wall = { ...w, id: id(), x1: midX, y1: midY };
    const nextWalls = [...wallsArr.filter((ww) => ww.id !== w.id), w1, w2];
    const nextNonWalls = nonWallsArr.map((o) => {
      if ((o.type === 'door' || o.type === 'window') && o.wallId === w.id) {
        if (o.position < t) return { ...o, wallId: w1.id, position: o.position / t };
        return { ...o, wallId: w2.id, position: t === 1 ? 0 : (o.position - t) / (1 - t) };
      }
      return o;
    });
    return { walls: nextWalls, nonWalls: nextNonWalls };
  }
  return { walls: wallsArr, nonWalls: nonWallsArr };
}

// ============================================
// Canvas
// ============================================

interface CanvasProps {
  objects: DrawingObject[];
  onObjectsChange: (objects: DrawingObject[]) => void;
  activeTool: Tool;
  snapSize: SnapSize;
  showGrid: boolean;
  layers: LayerType[];
  selectedWallType: string;
  onSelectedObjectChange?: (obj: DrawingObject | null) => void;
}

export default function DrawingCanvas({
  objects, onObjectsChange, activeTool, snapSize, showGrid, layers,
  selectedWallType, onSelectedObjectChange,
}: CanvasProps) {
  const stageRef = useRef<Konva.Stage>(null);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawPreview, setDrawPreview] = useState<{ x: number; y: number } | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [snapIndicator, setSnapIndicator] = useState<{ x: number; y: number } | null>(null);
  const [stageWidth, setStageWidth] = useState(typeof window !== 'undefined' ? window.innerWidth - 512 : 1200);
  const [stageHeight, setStageHeight] = useState(typeof window !== 'undefined' ? window.innerHeight - 60 : 800);
  const spaceHeldRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; spX: number; spY: number } | null>(null);

  // Refs to avoid stale closures in handleMouseMove
  const activeToolRef = useRef(activeTool);
  const isDrawingRef = useRef(isDrawing);
  const drawStartRef = useRef(drawStart);
  const snapSizeRef = useRef(snapSize);
  const objectsRef = useRef(objects);
  const selectedWallTypeRef = useRef(selectedWallType);
  activeToolRef.current = activeTool;
  selectedWallTypeRef.current = selectedWallType;
  isDrawingRef.current = isDrawing;
  drawStartRef.current = drawStart;
  snapSizeRef.current = snapSize;
  objectsRef.current = objects;

  // BUG-02: Handle window resize
  useEffect(() => {
    const onResize = () => {
      setStageWidth(window.innerWidth - 512);
      setStageHeight(window.innerHeight - 60);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const walls = objects.filter((o): o is Wall => o.type === 'wall');

  const toCanvas = useCallback((sx: number, sy: number) => {
    const st = stageRef.current;
    if (!st) return { x: 0, y: 0 };
    const p = st.getPointerPosition();
    if (!p) return { x: 0, y: 0 };
    return { x: (p.x - stagePos.x) / (stageScale * SCALE), y: (p.y - stagePos.y) / (stageScale * SCALE) };
  }, [stagePos, stageScale]);

  // ---- Mouse ----
  const handleMouseDown = useCallback((e: KonvaEventObject<MouseEvent>) => {
    // BUG-08: Skip canvas actions when panning (space or middle-click)
    if (spaceHeldRef.current || e.evt.button === 1) return;

    if (activeTool === 'select') {
      if (e.target === e.target.getStage() || e.target.getParent()?.attrs?.name === 'grid') {
        setSelId(null); onSelectedObjectChange?.(null);
      }
      return;
    }

    if (activeTool === 'wall') {
      const pos = toCanvas(e.evt.clientX, e.evt.clientY);
      let sn = doSnap(pos.x, pos.y, walls, snapSize);

      // Room-relative along-wall snap: only for placing the START of a new
      // wall (a T-junction on an existing wall) — see snapAlongHostWall.
      if (!isDrawing) {
        const nwc0 = getWallConstruction(selectedWallType);
        const nwThick0 = nwc0 ? nwc0.thickness / 1000 : 0.12;
        const hostSn = snapAlongHostWall(pos.x, pos.y, walls, snapSize, nwThick0);
        if (hostSn) sn = hostSn;
      }

      // Wall face snapping: align perpendicular component to exterior wall face
      if (isDrawing && drawStart) {
        const drawDx = pos.x - drawStart.x;
        const drawDy = pos.y - drawStart.y;
        const faceOff = getFaceOffset(pos.x, pos.y, drawDx, drawDy, selectedWallType, walls);
        if (faceOff) {
          const nwc = getWallConstruction(selectedWallType);
          const nwh = nwc ? nwc.thickness / 2000 : 0.06;
          sn = applyFaceSnap(sn.x, sn.y, snapSize, faceOff, nwh, faceOff.roomInnerWidth);
        }
      }

      if (!isDrawing) {
        setIsDrawing(true);
        setDrawStart(sn);
        setDrawPreview(sn);
        setSnapIndicator(null);
      } else if (drawStart) {
        const fin = snap45(drawStart.x, drawStart.y, sn.x, sn.y);
        // BUGFIX: snap45 already forces the angle to an exact 45°-multiple,
        // which for an axis-aligned wall (0/90/180/270°) makes ONE of
        // fin.x/fin.y come out EXACTLY equal to drawStart's. Re-snapping
        // BOTH coordinates to the grid independently here threw that away
        // (e.g. drawStart.x=4.917 but grid-snap rounds fin.x to 4.9),
        // introducing a small but real tilt — this was the "0.6°" bug.
        // Keep an axis pinned to drawStart's exact value when snap45 already
        // put it there; only grid-snap the axis that actually moved.
        const AXIS_EPS = 1e-6;
        let fx = Math.abs(fin.x - drawStart.x) < AXIS_EPS ? drawStart.x : snap(fin.x, snapSize);
        let fy = Math.abs(fin.y - drawStart.y) < AXIS_EPS ? drawStart.y : snap(fin.y, snapSize);

        // Re-apply face snap after re-snap to keep perpendicular component aligned
        const drawDx = pos.x - drawStart.x;
        const drawDy = pos.y - drawStart.y;
        const faceOff2 = getFaceOffset(pos.x, pos.y, drawDx, drawDy, selectedWallType, walls);
        if (faceOff2) {
          const nwc2 = getWallConstruction(selectedWallType);
          const nwh2 = nwc2 ? nwc2.thickness / 2000 : 0.06;
          const fs = applyFaceSnap(fx, fy, snapSize, faceOff2, nwh2, faceOff2.roomInnerWidth);
          fx = fs.x;
          fy = fs.y;
        }

        // Snap OUTER dimension to grid instead of center-to-center — but
        // ONLY when this end ISN'T already face-snapped to another wall.
        // When it IS (the common "wall spans all the way to the opposite
        // wall's face" case), applyFaceSnap already placed fx,fy exactly on
        // that face using its own room-relative grid snap; nudging it again
        // here to round (c2cLen + thisWall'sOwnThickness) overshoots past
        // the face by a few cm and corrupts the attachment — this was the
        // "shows 9.700 instead of 9.176" bug: the wall no longer actually
        // touches the wall it looks attached to.
        if (!faceOff2) {
          const wallConstruct = getWallConstruction(selectedWallType);
          const wallThick = wallConstruct ? wallConstruct.thickness / 1000 : 0.10;
          const dx = fx - drawStart.x, dy = fy - drawStart.y;
          const c2cLen = Math.sqrt(dx * dx + dy * dy);
          if (c2cLen > 0.001) {
            // Interior walls: snap the INNER (clear/net) length to the grid
            // instead of the outer one. For a free-floating interior wall
            // end there's no "corner" meaning to an outer length — what
            // actually matters is the clear space left over after this
            // wall's own thickness, since that's what the NEXT wall drawn
            // onward from here will have to fit into. Exterior walls keep
            // targeting the outer (footprint) length as before.
            const isIntWall = selectedWallType.startsWith('int');
            let adjust: number;
            if (isIntWall) {
              // BUGFIX: this used to subtract only this wall's OWN
              // thickness, ignoring what drawStart is actually attached
              // to — so "4.000" while dragging silently meant something
              // different (and smaller) once rendered against a thicker
              // host wall than itself. Now matches the render exactly:
              // also subtract the START's own connecting-wall adjustment,
              // so what you watch while dragging IS the final number.
              const startConnLive = findConnectingWallAt(drawStart.x, drawStart.y, dx, dy, null, walls);
              const startAdjLive = startConnLive ? (wallThick / 2 + wallThickness(startConnLive) / 2) : wallThick;
              const innerLen = Math.max(0, c2cLen - startAdjLive - wallThick);
              const innerSnapped = Math.round(innerLen / snapSize) * snapSize;
              adjust = (innerSnapped + wallThick + startAdjLive) - c2cLen;
            } else {
              const outerLen = c2cLen + wallThick;
              const outerSnapped = Math.round(outerLen / snapSize) * snapSize;
              adjust = outerSnapped - outerLen;
            }
            const dirX = dx / c2cLen, dirY = dy / c2cLen;
            fx += adjust * dirX;
            fy += adjust * dirY;
          }
        }

        if (d(drawStart.x, drawStart.y, fx, fy) > 0.01) {
          const newWall: Wall = {
            id: id(), type: 'wall',
            x1: drawStart.x, y1: drawStart.y, x2: fx, y2: fy,
            construction: selectedWallType, layer: 'construction',
          };

          // BUGFIX: split any existing wall at the new wall's START *and* END
          // point — handles T-junctions regardless of which direction you draw
          // (previously only the end point was checked, see trySplitWallAt).
          let finalWalls: Wall[] = objects.filter((o): o is Wall => o.type === 'wall');
          let updatedNonWalls: DrawingObject[] = objects.filter(o => o.type !== 'wall');
          ({ walls: finalWalls, nonWalls: updatedNonWalls } = trySplitWallAt(
            finalWalls, updatedNonWalls, drawStart.x, drawStart.y,
          ));
          ({ walls: finalWalls, nonWalls: updatedNonWalls } = trySplitWallAt(
            finalWalls, updatedNonWalls, fx, fy,
          ));
          finalWalls = [...finalWalls, newWall];

          onObjectsChange([...finalWalls, ...updatedNonWalls]);
          setDrawStart({ x: fx, y: fy });
          setDrawPreview({ x: fx, y: fy });
          return;
        }
        setIsDrawing(false); setDrawStart(null); setDrawPreview(null); setSnapIndicator(null);
      }
    }

    if (activeTool === 'door' || activeTool === 'window') {
      const pos = toCanvas(e.evt.clientX, e.evt.clientY);
      const cw = findNearWall(pos.x, pos.y);
      if (cw) {
        const wdx = cw.x2 - cw.x1, wdy = cw.y2 - cw.y1;
        const wallLen = Math.sqrt(wdx * wdx + wdy * wdy);
        const rawPos = wallT(cw, pos.x, pos.y);
        const itemWidth = activeTool === 'door' ? 0.8 : 1.2;
        // BUG-16: Clamp position so the door/window stays within wall bounds
        const halfFrac = wallLen > 0.001 ? (itemWidth / 2) / wallLen : 0;
        const clampedPos = Math.max(halfFrac, Math.min(1 - halfFrac, rawPos));
        const nw: Door | Window = activeTool === 'door'
          ? { id: id(), type: 'door', wallId: cw.id, position: clampedPos, width: 0.8, height: 2.1, layer: 'construction' }
          : { id: id(), type: 'window', wallId: cw.id, position: clampedPos, width: 1.2, height: 1.4, sillHeight: 0.9, layer: 'construction' };
        onObjectsChange([...objects, nw]);
      }
    }

    if (activeTool === 'erase') {
      const pos = toCanvas(e.evt.clientX, e.evt.clientY);
      const hit = findHit(pos.x, pos.y);
      if (hit) {
        if (hit.type === 'wall') {
          // BUG-03: cascade-delete doors/windows attached to this wall
          onObjectsChange(objects.filter((o) => o.id !== hit.id && !((o.type === 'door' || o.type === 'window') && o.wallId === hit.id)));
        } else {
          onObjectsChange(objects.filter((o) => o.id !== hit.id));
        }
      }
    }
  }, [activeTool, isDrawing, drawStart, objects, onObjectsChange, toCanvas, selectedWallType, onSelectedObjectChange, snapSize, walls]);

  // Use native event listener to guarantee mouse move fires
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const container = stage.container();
    if (!container) return;

    const onMove = (e: MouseEvent) => {
      // BUG-08: Handle panning
      if (panStartRef.current) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        setStagePos({ x: panStartRef.current.spX + dx, y: panStartRef.current.spY + dy });
        return;
      }
      const st = stageRef.current;
      if (!st) return;
      const pos = st.getPointerPosition();
      if (!pos) return;

      const tool = activeToolRef.current;
      const ss = snapSizeRef.current;
      const curObjects = objectsRef.current;
      const curWalls = curObjects.filter((o): o is Wall => o.type === 'wall');
      const sx = (pos.x - stagePos.x) / (stageScale * SCALE);
      const sy = (pos.y - stagePos.y) / (stageScale * SCALE);
      let sn = doSnap(sx, sy, curWalls, ss);

      // Room-relative along-wall snap: only for placing the START of a new
      // wall (a T-junction on an existing wall) — see snapAlongHostWall.
      let hostSnap: { x: number; y: number } | null = null;
      if (tool === 'wall' && !isDrawingRef.current) {
        const nwc0 = getWallConstruction(selectedWallTypeRef.current);
        const nwThick0 = nwc0 ? nwc0.thickness / 1000 : 0.12;
        hostSnap = snapAlongHostWall(sx, sy, curWalls, ss, nwThick0);
        if (hostSnap) sn = hostSnap;
      }

      // Wall face snapping: re-snap perpendicular component relative to
      // the nearest exterior wall's face (inner for interior walls, outer for exterior)
      if (tool === 'wall' && isDrawingRef.current && drawStartRef.current) {
        const selType = selectedWallTypeRef.current;
        const ds = drawStartRef.current;
        const faceInfo = getFaceOffset(sx, sy, sx - ds.x, sy - ds.y, selType, curWalls);
        if (faceInfo) {
          const nwc = getWallConstruction(selType);
          const nwh = nwc ? nwc.thickness / 2000 : 0.06;
          sn = applyFaceSnap(sn.x, sn.y, ss, faceInfo, nwh, faceInfo.roomInnerWidth);
        }
      }

      if (tool === 'wall') {
        // When face snap is active, use the face-snapped position directly
        // The alignment logic uses grid-snap which would overwrite face-snap
        const hasFaceSnap = isDrawingRef.current && drawStartRef.current &&
          getFaceOffset(sn.x, sn.y, sn.x - drawStartRef.current.x, sn.y - drawStartRef.current.y,
            selectedWallTypeRef.current, curWalls) !== null;

        if (hasFaceSnap) {
          setSnapIndicator(sn);
        } else if (hostSnap) {
          // Already room-relative snapped above — don't let the generic
          // wall-line alignment pass below re-snap it to the absolute grid.
          setSnapIndicator(sn);
        } else {
        let bestAlign: { x: number; y: number } | null = null;
        let bestDist = Infinity;
        for (const w of curWalls) {
          for (const pt of [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]) {
            if (isDrawingRef.current && drawStartRef.current && d(pt.x, pt.y, drawStartRef.current.x, drawStartRef.current.y) < 0.01) continue;
            const ddx = Math.abs(sn.x - pt.x);
            const ddy = Math.abs(sn.y - pt.y);
            if (ddx < 0.001) { if (ddy < bestDist) { bestDist = ddy; bestAlign = { x: pt.x, y: sn.y }; } }
            else if (ddy < 0.001) { if (ddx < bestDist) { bestDist = ddx; bestAlign = { x: sn.x, y: pt.y }; } }
          }
        }
        for (const w of curWalls) {
          const wdx = w.x2 - w.x1, wdy = w.y2 - w.y1;
          const wLenSq = wdx * wdx + wdy * wdy;
          if (wLenSq < 0.0001) continue;
          const t = Math.max(0, Math.min(1, ((sn.x - w.x1) * wdx + (sn.y - w.y1) * wdy) / wLenSq));
          const projX = w.x1 + t * wdx, projY = w.y1 + t * wdy;
          const distToWall = d(sn.x, sn.y, projX, projY);
          if (distToWall < 0.3 && distToWall > 0.01) {
            const sp = { x: snap(projX, ss), y: snap(projY, ss) };
            const dd = d(sn.x, sn.y, sp.x, sp.y);
            if (dd < bestDist) { bestDist = dd; bestAlign = sp; }
          }
        }
        let finalAlign = bestAlign;
        // Re-apply face snap to the aligned position
        if (bestAlign && isDrawingRef.current && drawStartRef.current) {
          const selType = selectedWallTypeRef.current;
          const ds = drawStartRef.current;
          const fInfo = getFaceOffset(bestAlign.x, bestAlign.y, bestAlign.x - ds.x, bestAlign.y - ds.y, selType, curWalls);
          if (fInfo) {
            const nwc = getWallConstruction(selType);
            const nwh = nwc ? nwc.thickness / 2000 : 0.06;
            finalAlign = applyFaceSnap(bestAlign.x, bestAlign.y, ss, fInfo, nwh, fInfo.roomInnerWidth);
          }
        }
        setSnapIndicator(finalAlign);
        } // end else (!hasFaceSnap)
      } else {
        setSnapIndicator(null);
      }

      if (isDrawingRef.current && drawStartRef.current) {
        const prev = snap45(drawStartRef.current.x, drawStartRef.current.y, sn.x, sn.y);
        // Same fix as in handleMouseDown: don't re-grid an axis snap45 already
        // pinned exactly to drawStart (axis-aligned walls), or the preview
        // shows a tiny tilt that the final click would then also inherit.
        const AXIS_EPS2 = 1e-6;
        let px = Math.abs(prev.x - drawStartRef.current.x) < AXIS_EPS2 ? drawStartRef.current.x : snap(prev.x, ss);
        let py = Math.abs(prev.y - drawStartRef.current.y) < AXIS_EPS2 ? drawStartRef.current.y : snap(prev.y, ss);

        // Apply outer dimension snap adjustment (same as in handleMouseDown)
        // so the preview and snap indicator show the correct room dimensions
        const curObjects = objectsRef.current;
        const curWalls = curObjects.filter((o): o is Wall => o.type === 'wall');
        const selWallType = activeToolRef.current === 'wall' ? selectedWallTypeRef.current : 'ext-standard';

        // Re-apply face snap after the re-snap to keep perpendicular component face-aligned
        const ds2 = drawStartRef.current;
        const faceInfo2 = getFaceOffset(sx, sy, sx - ds2.x, sy - ds2.y, selWallType, curWalls);
        if (faceInfo2) {
          const nwc3 = getWallConstruction(selWallType);
          const nwh3 = nwc3 ? nwc3.thickness / 2000 : 0.06;
          const fs = applyFaceSnap(px, py, ss, faceInfo2, nwh3, faceInfo2.roomInnerWidth);
          px = fs.x;
          py = fs.y;
        }

        // Skip the outer-dimension nudge when this end is already face-snapped
        // to another wall — applyFaceSnap above already placed px,py exactly
        // on that face via its own room-relative grid snap; nudging again to
        // round (length + thisWall'sOwnThickness) overshoots past the face
        // and was the "shows 9.700 instead of 9.176" bug.
        if (!faceInfo2) {
          const wallC = getWallConstruction(selWallType);
          const wThick = wallC ? wallC.thickness / 1000 : 0.10;
          const pdx = px - drawStartRef.current.x, pdy = py - drawStartRef.current.y;
          const pLen = Math.sqrt(pdx * pdx + pdy * pdy);
          if (pLen > 0.001) {
            // Same inner-vs-outer targeting swap as handleMouseDown, so the
            // live preview shows the number the final click will produce.
            const isIntWallPrev = selWallType.startsWith('int');
            let pAdjust: number;
            if (isIntWallPrev) {
              // Same start-point awareness as handleMouseDown — otherwise
              // the preview shows a different number than the final render.
              const startConnPrev = findConnectingWallAt(drawStartRef.current.x, drawStartRef.current.y, pdx, pdy, null, curWalls);
              const startAdjPrev = startConnPrev ? (wThick / 2 + wallThickness(startConnPrev) / 2) : wThick;
              const pInner = Math.max(0, pLen - startAdjPrev - wThick);
              const pInnerSnapped = Math.round(pInner / ss) * ss;
              pAdjust = (pInnerSnapped + wThick + startAdjPrev) - pLen;
            } else {
              const pOuter = pLen + wThick;
              const pOuterSnapped = Math.round(pOuter / ss) * ss;
              pAdjust = pOuterSnapped - pOuter;
            }
            const pDirX = pdx / pLen, pDirY = pdy / pLen;
            px += pAdjust * pDirX;
            py += pAdjust * pDirY;
          }
        }

        setDrawPreview({ x: px, y: py });
      }
    };

    // BUG-08: Panning handlers
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 1 || spaceHeldRef.current) { // middle-click or space held
        panStartRef.current = { x: e.clientX, y: e.clientY, spX: stagePos.x, spY: stagePos.y };
        e.preventDefault();
      }
    };
    const onMouseUp = () => { panStartRef.current = null; };

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [stagePos, stageScale]);

  const handleWheel = useCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const oldScale = stageScale;
    const newScale = e.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    // BUG-07: Adjust stagePos so the point under the cursor stays fixed during zoom
    const stage = stageRef.current;
    if (stage) {
      const pointer = stage.getPointerPosition();
      if (pointer) {
        const mousePointTo = {
          x: (pointer.x - stagePos.x) / oldScale,
          y: (pointer.y - stagePos.y) / oldScale,
        };
        setStagePos({
          x: pointer.x - mousePointTo.x * clampedScale,
          y: pointer.y - mousePointTo.y * clampedScale,
        });
      }
    }
    setStageScale(clampedScale);
  }, [stageScale, stagePos]);

  // BUG-08: Track space key for panning
  useEffect(() => {
    const kd = (e: KeyboardEvent) => { if (e.key === ' ') { spaceHeldRef.current = true; e.preventDefault(); } };
    const ku = (e: KeyboardEvent) => { if (e.key === ' ') { spaceHeldRef.current = false; panStartRef.current = null; } };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, []);

  useEffect(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setIsDrawing(false); setDrawStart(null); setDrawPreview(null); setSelId(null); setSnapIndicator(null); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selId && !(e.target instanceof HTMLInputElement)) {
        const deletedObj = objects.find(o => o.id === selId);
        if (deletedObj && deletedObj.type === 'wall') {
          // BUG-03: cascade-delete doors/windows attached to this wall
          onObjectsChange(objects.filter((o) => o.id !== selId && !((o.type === 'door' || o.type === 'window') && o.wallId === selId)));
        } else {
          onObjectsChange(objects.filter((o) => o.id !== selId));
        }
        setSelId(null); onSelectedObjectChange?.(null);
      }
    };
    window.addEventListener('keydown', kd);
    return () => window.removeEventListener('keydown', kd);
  }, [selId, objects, onObjectsChange, onSelectedObjectChange]);

  // ---- Helpers ----
  function findNearWall(x: number, y: number): Wall | null {
    let best: Wall | null = null, minD = 0.3;
    for (const w of walls) {
      const dd = ptWall(x, y, w);
      if (dd < minD) { minD = dd; best = w; }
    }
    return best;
  }

  function ptWall(px: number, py: number, w: Wall): number {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const ls = dx * dx + dy * dy;
    if (ls < 0.0001) return d(px, py, w.x1, w.y1);
    const t = Math.max(0, Math.min(1, ((px - w.x1) * dx + (py - w.y1) * dy) / ls));
    return d(px, py, w.x1 + t * dx, w.y1 + t * dy);
  }

  function wallT(w: Wall, px: number, py: number): number {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const ls = dx * dx + dy * dy;
    return ls < 0.0001 ? 0 : Math.max(0, Math.min(1, ((px - w.x1) * dx + (py - w.y1) * dy) / ls));
  }

  function findHit(x: number, y: number): DrawingObject | null {
    for (let i = objects.length - 1; i >= 0; i--) {
      const o = objects[i];
      if (o.type === 'wall' && ptWall(x, y, o) < 0.2) return o;
      if ((o.type === 'door' || o.type === 'window')) {
        const w = objects.find((ww) => ww.type === 'wall' && ww.id === o.wallId) as Wall | undefined;
        if (w && Math.abs(wallT(w, x, y) - o.position) < 0.1) return o;
      }
    }
    return null;
  }

  const handleClick = useCallback((obj: DrawingObject) => {
    if (activeTool === 'select') { setSelId(obj.id); onSelectedObjectChange?.(obj); }
    if (activeTool === 'erase') onObjectsChange(objects.filter((o) => o.id !== obj.id));
  }, [activeTool, objects, onObjectsChange, onSelectedObjectChange]);

  // ============================================
  // Render
  // ============================================

  const renderGrid = () => {
    if (!showGrid) return null;
    const lines: React.ReactNode[] = [];
    const gs = DEFAULT_GRID_SIZE;
    // BUG-11: Calculate visible viewport bounds and only render grid lines in that range
    const viewportLeft = -stagePos.x / stageScale / SCALE;
    const viewportTop = -stagePos.y / stageScale / SCALE;
    const viewportRight = (stageWidth - stagePos.x) / stageScale / SCALE;
    const viewportBottom = (stageHeight - stagePos.y) / stageScale / SCALE;
    const margin = gs * 2; // small margin for lines just outside viewport
    const startX = Math.floor((viewportLeft - margin) / gs) * gs;
    const endX = Math.ceil((viewportRight + margin) / gs) * gs;
    const startY = Math.floor((viewportTop - margin) / gs) * gs;
    const endY = Math.ceil((viewportBottom + margin) / gs) * gs;
    for (let i = startX; i <= endX; i += gs) {
      const m = Math.abs(i % 1) < 0.001;
      lines.push(<Line key={`v${i}`} points={[i * SCALE, startY * SCALE, i * SCALE, endY * SCALE]}
        stroke={m ? '#d0d0d0' : '#e8e8e8'} strokeWidth={m ? 0.5 : 0.25} listening={false} />);
    }
    for (let i = startY; i <= endY; i += gs) {
      const m = Math.abs(i % 1) < 0.001;
      lines.push(<Line key={`h${i}`} points={[startX * SCALE, i * SCALE, endX * SCALE, i * SCALE]}
        stroke={m ? '#d0d0d0' : '#e8e8e8'} strokeWidth={m ? 0.5 : 0.25} listening={false} />);
    }
    return lines;
  };

  const renderWall = (wall: Wall, isSelected: boolean) => {
    const thick = wallThickness(wall);
    const thickPx = thick * SCALE;
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.001) return null;
    const { nx, ny } = wallNormal(wall);
    const isExt = wall.construction.startsWith('ext');
    const dxn = dx / len, dyn = dy / len; // unit direction

    // Wall polygon — extend by half thickness at each end to fill corners
    const hw = thick / 2;
    const corners = [
      (wall.x1 - dxn * hw) + nx * hw, (wall.y1 - dyn * hw) + ny * hw,
      (wall.x2 + dxn * hw) + nx * hw, (wall.y2 + dyn * hw) + ny * hw,
      (wall.x2 + dxn * hw) - nx * hw, (wall.y2 + dyn * hw) - ny * hw,
      (wall.x1 - dxn * hw) - nx * hw, (wall.y1 - dyn * hw) - ny * hw,
    ].map(v => v * SCALE);

    // ── Dimension rendering ──
    const dimElements: React.ReactNode[] = [];

    if (isExt) {
      // ── EXTERIOR WALL: Y (yttermått) + I (innermått) ──
      const dimOuter = thickPx / 2 + 18; // Y offset — outside the building
      const dimInner = thickPx / 2 + 6;  // I offset — inside the building

      // BUGFIX: this wall may already be a SHORT PIECE that trySplitWallAt
      // physically split off at a T-junction with an interior wall — in
      // that case there's no interior split point found INSIDE this piece
      // (it would be right at this piece's own x1/x2, outside the (0.05,
      // 0.95) window below), so it always took this "no splits" branch and
      // used the symmetric len±thick formula at BOTH ends regardless of
      // whether either end is a real corner — producing a 2×thick gap even
      // at a plain T-junction end. Check each endpoint for a genuine
      // perpendicular exterior-wall corner instead of assuming one.
      const startAdj = cornerAdjustment(wall, wall.x1, wall.y1, objects);
      const endAdj = cornerAdjustment(wall, wall.x2, wall.y2, objects);
      const outerLen = len + startAdj + endAdj;
      const innerLen = Math.max(0, len - startAdj - endAdj);

      // Check for interior walls that split this exterior wall
      const splitWalls = objects.filter((o): o is Wall =>
        o.type === 'wall' && o.id !== wall.id && o.construction.startsWith('int')
      );
      // BUGFIX: a T-junction split point needs the ATTACHING interior
      // wall's own thickness recorded here — using ZERO (as before) measured
      // the segment all the way to the partition's bare CENTERLINE instead
      // of its near FACE, overshooting by exactly half the partition's
      // thickness (the "4971 instead of 4900" bug). This matches the
      // original snap-time model too: "available = roomInnerWidth minus the
      // new wall's own thickness" measures to faces on both sides, leaving
      // a gap for the new wall's body — the render must use the same model.
      const splitPoints: { t: number; thick: number }[] = [];
      for (const sw of splitWalls) {
        const t1 = pointOnWall(sw.x1, sw.y1, wall);
        if (t1 !== null && t1 > 0.05 && t1 < 0.95) splitPoints.push({ t: t1, thick: wallThickness(sw) });
        const t2 = pointOnWall(sw.x2, sw.y2, wall);
        if (t2 !== null && t2 > 0.05 && t2 < 0.95) splitPoints.push({ t: t2, thick: wallThickness(sw) });
      }
      splitPoints.sort((a, b) => a.t - b.t);

      // Build segments from split points, carrying each boundary's T-junction thickness
      const segments: { start: number; end: number; startThick: number; endThick: number }[] = [];
      let prev = 0;
      let prevThick = 0;
      for (const sp of splitPoints) {
        segments.push({ start: prev, end: sp.t, startThick: prevThick, endThick: sp.thick });
        prev = sp.t;
        prevThick = sp.thick;
      }
      segments.push({ start: prev, end: 1, startThick: prevThick, endThick: 0 });

      if (segments.length <= 1) {
        // No splits — single Y/I pair for the whole wall
        dimElements.push(renderDimPair(wall, nx, ny, dimOuter, dimInner, outerLen, innerLen, startAdj, endAdj));
      } else {
        // BUGFIX: the outer (Y) footprint number doesn't change no matter
        // how many interior walls attach to this wall — it's a structural
        // constant, so show it ONCE for the whole wall, same as an unsplit
        // wall. The inner (I) number DOES change per segment (that's the
        // whole point of a T-junction breakdown — showing where each
        // interior wall actually lands), so that one stays per-segment.
        // Showing BOTH overall AND per-segment for Y just doubled every Y
        // label with no new information.
        dimElements.push(renderOuterLine(wall, nx, ny, dimOuter, outerLen, startAdj, endAdj, '-overall'));

        // Split by interior walls — render I (inner) breakdown per segment
        for (const seg of segments) {
          const segLen = len * (seg.end - seg.start);

          // Boundary adjustment at segment start/end.
          // An INTERNAL split point (from the loop above, always an
          // interior wall by construction) is a T-junction, not a corner —
          // this wall's own face runs straight through untouched there, so
          // there's no adjustment for THIS wall's own thickness. But the
          // segment's clear-span NUMBER still needs to stop at the
          // attaching wall's near FACE, not overshoot to its bare
          // centerline — so use THAT wall's own thickness (seg.startThick/
          // endThick, recorded above), matching the snap-time model that
          // already measures "available room" to faces on both sides
          // (BUGFIX: previously used 0 here, which measured to the
          // partition's centerline instead of its face — the "4971 instead
          // of 4900" bug). Only this WALL'S OWN original x1/x2 endpoint
          // might be a genuine corner, and even then only if an exterior
          // wall actually turns there (cornerAdjustment checks that).
          const startBound = seg.start < 0.01 ? cornerAdjustment(wall, wall.x1, wall.y1, objects) * 2 : seg.startThick;
          const endBound = seg.end > 0.99 ? cornerAdjustment(wall, wall.x2, wall.y2, objects) * 2 : seg.endThick;

          const segInner = Math.max(0, segLen - startBound / 2 - endBound / 2);
          const sx = wall.x1 + dx * seg.start, sy = wall.y1 + dy * seg.start;
          const ex = wall.x1 + dx * seg.end, ey = wall.y1 + dy * seg.end;
          const segWallLike = { ...wall, x1: sx, y1: sy, x2: ex, y2: ey };

          dimElements.push(renderInnerLine(segWallLike, nx, ny, dimInner, segInner, startBound / 2, endBound / 2, `-seg${seg.start}`));
        }
      }
    } else {
      // ── INTERIOR WALL: Y (yttermått) + I (innermått) with junction adjustments ──
      const dimOuter = thickPx / 2 + 18;
      const dimInner = thickPx / 2 + 6;

      // Interior walls: per the user's decision (make the live
      // drag-preview geometrically true, redraw existing walls to match),
      // BOTH ends now use the same real face-to-face rule, and the
      // free-end SNAP (handleMouseDown/onMove) was updated to target this
      // exact same number while dragging — so what you watch while
      // dragging and what renders afterward are now guaranteed to be the
      // same value, for both the start AND end point:
      //   - FREE end (nothing there): subtract this wall's own FULL
      //     thickness.
      //   - ATTACHED end (terminates against another wall, start OR end):
      //     subtract HALF of this wall's own thickness + HALF of the
      //     OTHER wall's thickness — the true face-to-face clear span,
      //     since the endpoint sits on the other wall's CENTERLINE, not
      //     its face.
      // findConnectingWall detects a T-junction into the MIDDLE of a whole
      // (unsplit) wall's line too, not just a match at another wall's own
      // endpoint.
      const startConn = findConnectingWall(wall, wall.x1, wall.y1, objects);
      const startAdjInt = startConn ? (thick / 2 + wallThickness(startConn) / 2) : thick;
      const endConn = findConnectingWall(wall, wall.x2, wall.y2, objects);
      const endAdjInt = endConn ? (thick / 2 + wallThickness(endConn) / 2) : thick;
      const innerLen = Math.max(0, len - startAdjInt - endAdjInt);
      // Y stays I + this wall's own thickness, consistently.
      const outerLen = innerLen + thick;

      // The line's VISUAL extension is capped at the connecting wall's own
      // half-thickness (reaching its face exactly) — endAdjInt/startAdjInt
      // can be MORE than that (own half + their half), which would overshoot
      // straight through a thicker wall and out the other side.
      const startVisual = startConn ? wallThickness(startConn) / 2 : 0;
      const endVisualOuter = endConn ? wallThickness(endConn) / 2 : thick;

      // BUGFIX: I (blue) and Y (red) should consistently sit on the side
      // facing the larger/main room vs. the smaller room being carved out
      // — same convention exterior walls already use. nx/ny's sign is
      // whatever this wall's own draw direction happened to produce, which
      // is arbitrary for an interior wall; flip it toward whichever side
      // the bulk of the building actually is on.
      const roomSign = roomSideSign(wall, objects);
      const rnx = nx * roomSign, rny = ny * roomSign;

      dimElements.push(
        <Group key={`dim-${wall.id}`}>
          {renderInnerLine(wall, rnx, rny, dimInner, innerLen, startVisual, endAdjInt)}
          {renderOuterLine(wall, rnx, rny, dimOuter, outerLen, startVisual, endVisualOuter)}
        </Group>
      );
    }

    return (
      <Group key={wall.id}>
        {/* Wall body as polygon — clean corners */}
        <Line
          points={corners}
          closed
          fill={isExt ? '#4a4a4a' : '#7a7a7a'}
          stroke={isSelected ? '#2563eb' : '#555'}
          strokeWidth={isSelected ? 2 : 0.5}
          onClick={() => handleClick(wall)}
          onTap={() => handleClick(wall)}
        />
        {dimElements}
        <Circle x={wall.x1 * SCALE} y={wall.y1 * SCALE} radius={3} fill="#555" listening={false} />
        <Circle x={wall.x2 * SCALE} y={wall.y2 * SCALE} radius={3} fill="#555" listening={false} />
      </Group>
    );
  };

  // ── Y/I dimension pair for exterior walls ──
  // Y = Yttermått (red, outer face) — placed on -nx side (outside building)
  // I = Innermått (blue, inner face) — placed on +nx side (inside building)
  //
  // BUGFIX: the dimension LINES used to always span the wall's bare
  // centerline (wall.x1..wall.x2) regardless of what number was printed on
  // them — so a "Y 10.000" line visually stopped short of the real outer
  // corners, and "I 9.176" visually overshot past the real inner faces,
  // which looked misleading. Extend/inset each end by startAdj/endAdj
  // (half-thickness-equivalent amounts the caller already used to compute
  // outerLen/innerLen) so the line itself actually reaches the real corner
  // (Y) or real face (I), matching the number it's labelled with — using
  // the SAME per-end amounts as the number, not a separately recomputed
  // (and therefore potentially inconsistent) connecting-wall lookup.
  // Split into independent outer/inner renderers so callers can choose to
  // show just one side — e.g. an exterior wall split by interior walls
  // shows ONE overall Y (outer) for the whole wall, but a separate I
  // (inner) breakdown per segment, not both doubled for every segment.
  function renderOuterLine(wall: Wall, nx: number, ny: number, dimOuter: number, outerLen: number, startAdj: number, endAdj: number, keySuffix = '') {
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
    const wlen = Math.hypot(dx, dy);
    const dxn = wlen > 0.0001 ? dx / wlen : 0, dyn = wlen > 0.0001 ? dy / wlen : 0;
    const oy1x = wall.x1 - dxn * startAdj, oy1y = wall.y1 - dyn * startAdj;
    const oy2x = wall.x2 + dxn * endAdj, oy2y = wall.y2 + dyn * endAdj;
    return (
      <Group key={`dimY-${wall.id}${keySuffix}`}>
        <Line points={[
          oy1x * SCALE - nx * dimOuter, oy1y * SCALE - ny * dimOuter,
          oy2x * SCALE - nx * dimOuter, oy2y * SCALE - ny * dimOuter,
        ]} stroke="#e11d48" strokeWidth={0.8} listening={false} />
        <Line points={[
          oy1x * SCALE - nx * (dimOuter - 4), oy1y * SCALE - ny * (dimOuter - 4),
          oy1x * SCALE - nx * (dimOuter + 4), oy1y * SCALE - ny * (dimOuter + 4),
        ]} stroke="#e11d48" strokeWidth={0.8} listening={false} />
        <Line points={[
          oy2x * SCALE - nx * (dimOuter - 4), oy2y * SCALE - ny * (dimOuter - 4),
          oy2x * SCALE - nx * (dimOuter + 4), oy2y * SCALE - ny * (dimOuter + 4),
        ]} stroke="#e11d48" strokeWidth={0.8} listening={false} />
        <Text
          x={(oy1x + oy2x) / 2 * SCALE - nx * (dimOuter + 10) - 20}
          y={(oy1y + oy2y) / 2 * SCALE - ny * (dimOuter + 10) - 5}
          text={`Y ${(Math.round(outerLen * 1000) / 1000).toFixed(3)}`} fontSize={8} fill="#e11d48" fontFamily="monospace" listening={false}
        />
      </Group>
    );
  }

  function renderInnerLine(wall: Wall, nx: number, ny: number, dimInner: number, innerLen: number, startAdj: number, endAdj: number, keySuffix = '') {
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
    const wlen = Math.hypot(dx, dy);
    const dxn = wlen > 0.0001 ? dx / wlen : 0, dyn = wlen > 0.0001 ? dy / wlen : 0;
    const iy1x = wall.x1 + dxn * startAdj, iy1y = wall.y1 + dyn * startAdj;
    const iy2x = wall.x2 - dxn * endAdj, iy2y = wall.y2 - dyn * endAdj;
    return (
      <Group key={`dimI-${wall.id}${keySuffix}`}>
        <Line points={[
          iy1x * SCALE + nx * dimInner, iy1y * SCALE + ny * dimInner,
          iy2x * SCALE + nx * dimInner, iy2y * SCALE + ny * dimInner,
        ]} stroke="#2563eb" strokeWidth={0.8} listening={false} />
        <Line points={[
          iy1x * SCALE + nx * (dimInner - 4), iy1y * SCALE + ny * (dimInner - 4),
          iy1x * SCALE + nx * (dimInner + 4), iy1y * SCALE + ny * (dimInner + 4),
        ]} stroke="#2563eb" strokeWidth={0.8} listening={false} />
        <Line points={[
          iy2x * SCALE + nx * (dimInner - 4), iy2y * SCALE + ny * (dimInner - 4),
          iy2x * SCALE + nx * (dimInner + 4), iy2y * SCALE + ny * (dimInner + 4),
        ]} stroke="#2563eb" strokeWidth={0.8} listening={false} />
        <Text
          x={(iy1x + iy2x) / 2 * SCALE + nx * (dimInner + 10) - 20}
          y={(iy1y + iy2y) / 2 * SCALE + ny * (dimInner + 10) - 5}
          text={`I ${(Math.round(innerLen * 1000) / 1000).toFixed(3)}`} fontSize={8} fill="#2563eb" fontFamily="monospace" listening={false}
        />
      </Group>
    );
  }

  function renderDimPair(
    wall: Wall, nx: number, ny: number, dimOuter: number, dimInner: number,
    outerLen: number, innerLen: number, startAdj: number, endAdj: number,
  ) {
    return (
      <Group key={`dim-${wall.id}`}>
        {renderInnerLine(wall, nx, ny, dimInner, innerLen, startAdj, endAdj)}
        {renderOuterLine(wall, nx, ny, dimOuter, outerLen, startAdj, endAdj)}
      </Group>
    );
  }

  function pointOnWall(px: number, py: number, wall: Wall): number | null {
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
    const ls = dx * dx + dy * dy;
    if (ls < 0.0001) return null;
    const t = ((px - wall.x1) * dx + (py - wall.y1) * dy) / ls;
    const projX = wall.x1 + t * dx, projY = wall.y1 + t * dy;
    if (d(px, py, projX, projY) > 0.15) return null;
    return t;
  }

  const renderDoor = (door: Door) => {
    const w = objects.find((o) => o.type === 'wall' && o.id === door.wallId) as Wall | undefined;
    if (!w) return null;
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const px = w.x1 + dx * door.position, py = w.y1 + dy * door.position;
    const a = Math.atan2(dy, dx);
    return (
      <Group key={door.id} x={px * SCALE} y={py * SCALE} rotation={(a * 180) / Math.PI}>
        <Rect x={-door.width * SCALE * 0.5} y={-4} width={door.width * SCALE} height={8}
          fill="#fff" stroke={selId === door.id ? '#2563eb' : '#333'}
          strokeWidth={selId === door.id ? 2 : 0.5}
          onClick={() => handleClick(door)} onTap={() => handleClick(door)} />
        {/* BUG-14: Quarter-circle arc for door swing */}
        <Arc x={door.width * SCALE * 0.5} y={0}
          innerRadius={door.width * SCALE * 0.7} outerRadius={door.width * SCALE * 0.7}
          angle={90} rotation={-90}
          stroke="#999" strokeWidth={0.5} dash={[3, 3]} listening={false} />
      </Group>
    );
  };

  const renderWindow = (win: Window) => {
    const w = objects.find((o) => o.type === 'wall' && o.id === win.wallId) as Wall | undefined;
    if (!w) return null;
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const px = w.x1 + dx * win.position, py = w.y1 + dy * win.position;
    const a = Math.atan2(dy, dx);
    return (
      <Group key={win.id} x={px * SCALE} y={py * SCALE} rotation={(a * 180) / Math.PI}>
        <Rect x={-win.width * SCALE * 0.5} y={-3} width={win.width * SCALE} height={6}
          fill="#add8e6" stroke={selId === win.id ? '#2563eb' : '#333'}
          strokeWidth={selId === win.id ? 2 : 0.5}
          onClick={() => handleClick(win)} onTap={() => handleClick(win)} />
        <Line points={[0, -3, 0, 3]} stroke="#666" strokeWidth={0.5} listening={false} />
        <Line points={[-win.width * SCALE * 0.5, 0, win.width * SCALE * 0.5, 0]}
          stroke="#666" strokeWidth={0.5} listening={false} />
      </Group>
    );
  };

  // Render snap indicator — works even BEFORE drawing starts
  const renderSnapIndicator = () => {
    if (!snapIndicator || activeToolRef.current !== 'wall') return null;
    const si = snapIndicator;
    const ext = 2000;

    // Calculate room dimensions on each side of the snap position
    // Shows distances to PERPENDICULAR walls at each end of the host wall
    const distLabels: React.ReactNode[] = [];
    const curWalls = objectsRef.current.filter((o): o is Wall => o.type === 'wall');

    // New wall thickness from the currently selected wall type
    const newWallConstruct = getWallConstruction(selectedWallType);
    const newWallThick = newWallConstruct ? newWallConstruct.thickness / 1000 : 0.10;
    const newWallHalf = newWallThick / 2;

    // Find the host wall (the wall the snap point is on)
    for (const w of curWalls) {
      const wdx = w.x2 - w.x1, wdy = w.y2 - w.y1;
      const wLenSq = wdx * wdx + wdy * wdy;
      if (wLenSq < 0.0001) continue;
      const t = ((si.x - w.x1) * wdx + (si.y - w.y1) * wdy) / wLenSq;
      if (t < 0.01 || t > 0.99) continue;
      const projX = w.x1 + t * wdx, projY = w.y1 + t * wdy;
      const distToWall = d(si.x, si.y, projX, projY);
      if (distToWall > 0.01) {
        continue;
      }

      const wLen = Math.sqrt(wLenSq);
      const dirX = wdx / wLen, dirY = wdy / wLen;
      const normX = -dirY, normY = dirX;

      // Find perpendicular walls at each end of the host wall
      // These are walls whose endpoint is within 15cm of the host wall's endpoint
      const endThresh = 0.15;
      let leftPerpHalf = 0.15; // default exterior wall half thickness
      let rightPerpHalf = 0.15;
      let foundLeft = false, foundRight = false;

      // Take the THICKEST connecting wall at each end (consistent with getFaceOffset's
      // Math.max logic) instead of whichever wall happens to be last in iteration order —
      // the previous "last write wins" version made the room-width indicator order-dependent
      // and could disagree with the thickness actually applied by face snapping.
      for (const ow of curWalls) {
        if (ow.id === w.id) continue;
        const owHalf = wallThickness(ow) / 2;
        for (const owPt of [{ x: ow.x1, y: ow.y1 }, { x: ow.x2, y: ow.y2 }]) {
          if (d(owPt.x, owPt.y, w.x1, w.y1) < endThresh) {
            leftPerpHalf = foundLeft ? Math.max(leftPerpHalf, owHalf) : owHalf;
            foundLeft = true;
          }
          if (d(owPt.x, owPt.y, w.x2, w.y2) < endThresh) {
            rightPerpHalf = foundRight ? Math.max(rightPerpHalf, owHalf) : owHalf;
            foundRight = true;
          }
        }
      }

      // If no perpendicular walls found at an end, the host wall IS the boundary
      // (snap is on the host wall, so room extends inward)
      if (!foundLeft) leftPerpHalf = 0;
      if (!foundRight) rightPerpHalf = 0;

      // Room inner width = center-to-center - leftPerpHalf - rightPerpHalf
      const roomInnerWidth = Math.max(0, wLen - leftPerpHalf - rightPerpHalf);

      // Snap position along host wall from (x1, y1)
      const snapDist = (si.x - w.x1) * dirX + (si.y - w.y1) * dirY;
      // Snap position relative to left inner face
      const snapFromLeftInner = snapDist - leftPerpHalf;

      // Remaining clear room on each side after placing the new wall
      const leftRoom = Math.max(0, snapFromLeftInner - newWallHalf);
      const rightRoom = Math.max(0, roomInnerWidth - snapFromLeftInner - newWallHalf);

      // Position labels along the host wall direction, offset perpendicular for visibility
      const perpOffset = 20; // pixels offset perpendicular to host wall
      if (leftRoom > 0.01) {
        const leftMidDist = leftPerpHalf + leftRoom / 2;
        const lx = (w.x1 + leftMidDist * dirX) * SCALE + normX * perpOffset;
        const ly = (w.y1 + leftMidDist * dirY) * SCALE + normY * perpOffset;
        distLabels.push(
          <React.Fragment key={`distA-${w.id}`}>
            <Rect x={lx - 28} y={ly - 10} width={60} height={20}
              cornerRadius={3} fill="rgba(22,163,74,0.15)" listening={false} />
            <Text x={lx - 26} y={ly - 8}
              text={`${(Math.round(leftRoom * 1000) / 1000).toFixed(3)} m`} fontSize={13} fill="#16a34a" fontFamily="monospace"
              fontStyle="bold" listening={false} />
          </React.Fragment>
        );
      }
      if (rightRoom > 0.01) {
        const rightMidDist = snapDist + newWallHalf + rightRoom / 2;
        const rx = (w.x1 + rightMidDist * dirX) * SCALE + normX * perpOffset;
        const ry = (w.y1 + rightMidDist * dirY) * SCALE + normY * perpOffset;
        distLabels.push(
          <React.Fragment key={`distB-${w.id}`}>
            <Rect x={rx - 28} y={ry - 10} width={60} height={20}
              cornerRadius={3} fill="rgba(22,163,74,0.15)" listening={false} />
            <Text x={rx - 26} y={ry - 8}
              text={`${(Math.round(rightRoom * 1000) / 1000).toFixed(3)} m`} fontSize={13} fill="#16a34a" fontFamily="monospace"
              fontStyle="bold" listening={false} />
          </React.Fragment>
        );
      }
    }

    return (
      <Group>
        {/* Vertical guide */}
        <Line points={[si.x * SCALE, -ext * SCALE, si.x * SCALE, ext * SCALE]}
          stroke="#f59e0b" strokeWidth={1.5} dash={[10, 6]} listening={false} />
        {/* Horizontal guide */}
        <Line points={[-ext * SCALE, si.y * SCALE, ext * SCALE, si.y * SCALE]}
          stroke="#f59e0b" strokeWidth={1.5} dash={[10, 6]} listening={false} />
        {/* Crosshair */}
        <Group x={si.x * SCALE} y={si.y * SCALE}>
          <Line points={[-20, 0, 20, 0]} stroke="#f59e0b" strokeWidth={2.5} listening={false} />
          <Line points={[0, -20, 0, 20]} stroke="#f59e0b" strokeWidth={2.5} listening={false} />
          <Circle radius={10} stroke="#f59e0b" strokeWidth={2.5} fill="transparent" listening={false} />
          <Circle radius={4} fill="#f59e0b" listening={false} />
        </Group>
        {distLabels}
      </Group>
    );
  };

  // Drawing preview with live length
  const renderPreview = () => {
    if (!isDrawing || !drawStart || !drawPreview) return null;
    const dx = drawPreview.x - drawStart.x, dy = drawPreview.y - drawStart.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const mx = (drawStart.x + drawPreview.x) / 2 * SCALE;
    const my = (drawStart.y + drawPreview.y) / 2 * SCALE;
    const elements: React.ReactNode[] = [];

    // Preview line
    elements.push(
      <Line key="prev" points={[drawStart.x * SCALE, drawStart.y * SCALE, drawPreview.x * SCALE, drawPreview.y * SCALE]}
        stroke="#2563eb" strokeWidth={3} dash={[6, 3]} />
    );
    elements.push(
      <Circle key="start" x={drawStart.x * SCALE} y={drawStart.y * SCALE} radius={4} fill="#2563eb" stroke="#fff" strokeWidth={1} />
    );

    // Length label — interior walls show the INNER (clear) dimension since
    // that's what the free end actually gets snapped to now; exterior
    // walls keep showing the OUTER (footprint) dimension as before.
    if (len > 0.01) {
      const selConstruction = getWallConstruction(selectedWallType);
      const wallThick = selConstruction ? selConstruction.thickness / 1000 : 0.3;
      const isIntPreview = selectedWallType.startsWith('int');
      let labelDisplay: number;
      if (isIntPreview) {
        // Same start-point awareness as handleMouseDown/onMove's snap, so
        // this badge shows the exact number that'll actually render.
        const startConnBadge = findConnectingWallAt(drawStart.x, drawStart.y, dx, dy, null, objectsRef.current.filter((o): o is Wall => o.type === 'wall'));
        const startAdjBadge = startConnBadge ? (wallThick / 2 + wallThickness(startConnBadge) / 2) : wallThick;
        const innerRaw = Math.max(0, len - startAdjBadge - wallThick);
        labelDisplay = Math.round(innerRaw / snapSizeRef.current) * snapSizeRef.current;
      } else {
        const outerRaw = len + wallThick;
        labelDisplay = Math.round(outerRaw / snapSizeRef.current) * snapSizeRef.current;
      }
      elements.push(
        <Group key="len" x={mx} y={my - 14}>
          <Rect x={-32} y={-8} width={64} height={16} fill="#1e40af" cornerRadius={3} />
          <Text x={-30} y={-6} text={`${(Math.round(labelDisplay * 1000) / 1000).toFixed(3)} m`} fontSize={10} fill="#fff" fontFamily="monospace" />
        </Group>
      );
      // ESC hint at the end of the wall (near cursor position)
      const endX = drawPreview.x * SCALE;
      const endY = drawPreview.y * SCALE;
      elements.push(
        <Group key="esc-hint" x={endX + 16} y={endY + 16}>
          <Text x={0} y={0} text="ESC = avbryt" fontSize={9} fill="#999" fontFamily="monospace" listening={false} />
        </Group>
      );
    }

    // Snap indicator — show guide lines when aligned with ANY wall endpoint on X or Y axis
    if (snapIndicator) {
      const si = snapIndicator;
      const ext = 2000;
      // Vertical guide through snap point X
      elements.push(
        <Line key="snapGV" points={[si.x * SCALE, -ext * SCALE, si.x * SCALE, ext * SCALE]}
          stroke="#f59e0b" strokeWidth={1} dash={[8, 6]} listening={false} opacity={0.7} />
      );
      // Horizontal guide through snap point Y
      elements.push(
        <Line key="snapGH" points={[-ext * SCALE, si.y * SCALE, ext * SCALE, si.y * SCALE]}
          stroke="#f59e0b" strokeWidth={1} dash={[8, 6]} listening={false} opacity={0.7} />
      );
      // Crosshair at snap point
      elements.push(
        <Group key="snap" x={si.x * SCALE} y={si.y * SCALE}>
          <Line points={[-16, 0, 16, 0]} stroke="#f59e0b" strokeWidth={2} listening={false} />
          <Line points={[0, -16, 0, 16]} stroke="#f59e0b" strokeWidth={2} listening={false} />
          <Circle radius={8} stroke="#f59e0b" strokeWidth={2} fill="transparent" listening={false} />
          <Circle radius={3} fill="#f59e0b" listening={false} />
        </Group>
      );
    }

    // Room dimension preview when near an existing wall
    if (len > 0.1) {
      const nearW = findNearWall(drawPreview.x, drawPreview.y);
      if (nearW) {
        const nwThick = wallThickness(nearW);
        const ndx = nearW.x2 - nearW.x1, ndy = nearW.y2 - nearW.y1;
        const nLen = Math.sqrt(ndx * ndx + ndy * ndy);
        if (nLen > 0.001) {
          const perpX = -ndy / nLen, perpY = ndx / nLen;
          const sideA = Math.abs((drawPreview.x - nearW.x1) * perpX + (drawPreview.y - nearW.y1) * perpY) - nwThick / 2;
          const sideB = Math.abs((drawPreview.x - nearW.x1) * (-perpX) + (drawPreview.y - nearW.y1) * (-perpY)) - nwThick / 2;
          if (sideA > 0.1) {
            elements.push(
              <Text key="rmA" x={drawPreview.x * SCALE + perpX * 30} y={drawPreview.y * SCALE + perpY * 30 - 5}
                text={`${(Math.round(sideA * 1000) / 1000).toFixed(3)}m`} fontSize={8} fill="#16a34a" fontFamily="monospace" listening={false} />
            );
          }
          if (sideB > 0.1) {
            elements.push(
              <Text key="rmB" x={drawPreview.x * SCALE - perpX * 30} y={drawPreview.y * SCALE - perpY * 30 - 5}
                text={`${(Math.round(sideB * 1000) / 1000).toFixed(3)}m`} fontSize={8} fill="#16a34a" fontFamily="monospace" listening={false} />
            );
          }
        }
      }
    }

    // Face-to-face distance: show distance from new wall to nearby exterior wall's face
    // when face snapping is active
    if (len > 0.1 && drawStart && drawPreview) {
      const extWalls = objects.filter((o): o is Wall => o.type === 'wall' && o.construction.startsWith('ext'));
      const newIsInt = selectedWallType.startsWith('int');
      for (const ew of extWalls) {
        const ewDx = ew.x2 - ew.x1, ewDy = ew.y2 - ew.y1;
        const ewLen = Math.sqrt(ewDx * ewDx + ewDy * ewDy);
        if (ewLen < 0.001) continue;
        // Distance from midpoint of preview to wall
        const midX = (drawStart.x + drawPreview.x) / 2;
        const midY = (drawStart.y + drawPreview.y) / 2;
        const t = Math.max(0, Math.min(1, ((midX - ew.x1) * ewDx + (midY - ew.y1) * ewDy) / (ewLen * ewLen)));
        const projX = ew.x1 + t * ewDx;
        const projY = ew.y1 + t * ewDy;
        const faceDist = Math.sqrt((midX - projX) ** 2 + (midY - projY) ** 2);
        if (faceDist > FACE_SNAP_THRESHOLD) continue;
        // Only show when drawing roughly parallel
        const drawLen2 = Math.sqrt(dx * dx + dy * dy);
        if (drawLen2 < 0.001) continue;
        const cosA = Math.abs((dx / drawLen2) * (ewDx / ewLen) + (dy / drawLen2) * (ewDy / ewLen));
        if (cosA < FACE_SNAP_ALIGN_THRESHOLD) continue;
        // Calculate face-to-face perpendicular distance
        const { nx: ewNx, ny: ewNy } = wallNormal(ew);
        const ewThick = wallThickness(ew);
        const newThick = wallThickness({ construction: selectedWallType } as Wall);
        // Signed distance from midpoint to wall center on normal
        const signedDist = (midX - ew.x1) * ewNx + (midY - ew.y1) * ewNy;
        const faceHalf = newIsInt ? ewThick / 2 : -ewThick / 2;
        const newFaceHalf = signedDist > 0 ? newThick / 2 : -newThick / 2;
        const faceToFace = Math.abs(signedDist) - Math.abs(faceHalf) - Math.abs(newFaceHalf);
        if (faceToFace > 0.01) {
          const labelX = midX * SCALE + ewNx * (signedDist > 0 ? 1 : -1) * 35;
          const labelY = midY * SCALE + ewNy * (signedDist > 0 ? 1 : -1) * 35 - 5;
          elements.push(
            <Text key="faceDist" x={labelX} y={labelY}
              text={`⇔ ${(Math.round(faceToFace * 1000) / 1000).toFixed(3)}m`} fontSize={9} fill="#7c3aed" fontFamily="monospace"
              fontStyle="bold" listening={false} />
          );
          break; // only show for nearest exterior wall
        }
      }
    }

    return <Group>{elements}</Group>;
  };

  return (
    <Stage ref={stageRef}
      width={stageWidth}
      height={stageHeight}
      onMouseDown={handleMouseDown} onWheel={handleWheel}
      scaleX={stageScale} scaleY={stageScale} x={stagePos.x} y={stagePos.y}
      style={{ background: '#f8f8f8' }}
    >
      <Layer>
        {renderGrid()}
        {renderSnapIndicator()}
        {renderPreview()}
        {objects.map((o) => {
          if (o.type === 'wall') return renderWall(o, selId === o.id);
          if (o.type === 'door') return renderDoor(o);
          if (o.type === 'window') return renderWindow(o);
          return null;
        })}
        <Circle x={0} y={0} radius={3} fill="#ef4444" />
        <Text x={5} y={-10} text="0,0" fontSize={8} fill="#ef4444" />
      </Layer>
    </Stage>
  );
}
